// apps/admin/src/pages/AdminAuditLogPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { useAdminAuth } from '../contexts/AdminContext';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import { FileText, User, Building2, AlertTriangle, Info, AlertCircle, ChevronDown, ChevronUp, Filter, Search, X, Shield, History, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface AdminAuditLog {
  id: string;
  timestamp: string;
  adminUid: string;
  adminEmail: string;
  adminName: string;
  action: string;
  resource: string;
  resourceName?: string;
  companyId?: string;
  companyName?: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  reason?: string;
  ipAddress?: string;
  userAgent?: string;
  affectedUsers?: string[];
  changes?: Record<string, any>;
  metadata?: Record<string, any>;
  dataExported?: {
    type: string;
    format: string;
    recordCount: number;
    fileSizeMB: number;
  };
  regulatoryTag: string[];
}

interface Filters {
  adminUid?: string;
  companyId?: string;
  action?: string;
  severity?: 'INFO' | 'WARNING' | 'CRITICAL';
  startDate?: string;
  endDate?: string;
}

const AdminAuditLogPage: React.FC = () => {
  const { user } = useAdminAuth();
  const { logAction } = useAdminAuditLog();
  const [logs, setLogs] = useState<AdminAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const getAdminAuditLogs = httpsCallable(functions, 'getAdminAuditLogs');

      const result = await getAdminAuditLogs({
        limit: 100,
        filters,
      });

      const data = result.data as { logs: AdminAuditLog[] };
      setLogs(data.logs);
    } catch (err: any) {
      console.error('Error fetching admin audit logs:', err);
      // Firebase callable functions errors have message in err.message
      const errorMessage = err?.message || err?.code || 'Failed to load admin audit logs';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Log viewing of admin audit logs (Privacy Act APP 13 compliance)
  useEffect(() => {
    void logAction({
      action: 'ADMIN_SECURITY_ALERT_VIEWED',
      resource: 'admin_audit_logs',
      resourceName: 'Admin Audit Logs',
      metadata: {
        view: 'admin_audit_logs',
      },
    });
  }, [logAction]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-900 text-red-200';
      case 'WARNING':
        return 'bg-orange-900 text-orange-200';
      default:
        return 'bg-blue-900 text-blue-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle className="w-5 h-5" />;
      case 'WARNING':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const formatAction = (action: string) => {
    return action
      .replace('ADMIN_', '')
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (timestamp: string) => {
    if (!timestamp) {
      console.warn('[AdminAuditLog] Missing timestamp in audit log entry');
      return 'Unknown time';
    }
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.warn('[AdminAuditLog] Invalid timestamp:', timestamp);
        return 'Unknown time';
      }
      const absolute = date.toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const relative = formatDistanceToNow(date, { addSuffix: true });
      return `${absolute} (${relative})`;
    } catch (e) {
      console.error('[AdminAuditLog] Failed to parse timestamp:', timestamp, e);
      return 'Unknown time';
    }
  };

  const applyFilter = (key: keyof Filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.adminEmail.toLowerCase().includes(search) ||
      log.action.toLowerCase().includes(search) ||
      log.companyName?.toLowerCase().includes(search) ||
      log.resourceName?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-white">Loading Admin Audit Logs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6 text-white">
      {/* Gradient Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Admin Audit Log</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Global audit trail of all admin operations across all companies. Retention: 10 years (legal compliance).
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm">Privacy Act APP 13</span>
            </div>
            <div className="flex items-center gap-2">
              <History className="w-4 h-4" />
              <span className="text-sm">10-year retention</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Founder access only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by admin, action, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:border-blue-500 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Severity</label>
                <select
                  value={filters.severity || ''}
                  onChange={(e) => applyFilter('severity', e.target.value as any)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Severities</option>
                  <option value="INFO">Info</option>
                  <option value="WARNING">Warning</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => applyFilter('startDate', e.target.value)}
                  style={{ colorScheme: 'dark' }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => applyFilter('endDate', e.target.value)}
                  style={{ colorScheme: 'dark' }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-700 rounded-lg text-red-200">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="text-sm text-gray-400">Total Logs</div>
          <div className="text-2xl font-bold text-white">{filteredLogs.length}</div>
        </div>
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="text-sm text-gray-400">Critical</div>
          <div className="text-2xl font-bold text-red-400">
            {filteredLogs.filter(l => l.severity === 'CRITICAL').length}
          </div>
        </div>
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="text-sm text-gray-400">Warnings</div>
          <div className="text-2xl font-bold text-orange-400">
            {filteredLogs.filter(l => l.severity === 'WARNING').length}
          </div>
        </div>
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="text-sm text-gray-400">Info</div>
          <div className="text-2xl font-bold text-blue-400">
            {filteredLogs.filter(l => l.severity === 'INFO').length}
          </div>
        </div>
      </div>

      {/* Logs */}
      {filteredLogs.length === 0 ? (
        <div className="bg-gray-800 p-12 rounded-lg shadow-lg text-center border border-gray-700">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Audit Logs</h3>
          <p className="text-gray-400">No admin operations match your filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLogs.map((log) => {
            const isExpanded = expandedLogs.has(log.id);
            return (
              <div
                key={log.id}
                className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 hover:border-gray-600 transition-colors"
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Severity Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full ${getSeverityColor(log.severity)} flex items-center justify-center font-bold`}>
                      {getSeverityIcon(log.severity)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Description */}
                      <p className="text-white text-base leading-relaxed mb-2">
                        <span className="font-semibold">{log.adminName}</span>
                        {' '}
                        <span className="text-gray-300">{formatAction(log.action)}</span>
                        {log.companyName && (
                          <span className="text-gray-300">
                            {' '}on <span className="font-semibold text-blue-400">{log.companyName}</span>
                          </span>
                        )}
                        {log.resourceName && log.resourceName !== log.companyName && (
                          <span className="text-gray-400"> ({log.resourceName})</span>
                        )}
                      </p>

                      {/* Metadata row */}
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {log.adminEmail}
                        </span>
                        {log.companyId && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {log.companyId.substring(0, 8)}...
                          </span>
                        )}
                        <span>{formatDate(log.timestamp)}</span>
                        <button
                          onClick={() => toggleExpanded(log.id)}
                          className="ml-auto text-blue-400 hover:text-blue-300 transition-colors text-xs"
                        >
                          {isExpanded ? '▼ Hide details' : '▶ Show details'}
                        </button>
                      </div>

                      {/* Reason (if exists) */}
                      {log.reason && (
                        <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg">
                          <div className="text-xs font-semibold text-yellow-400 mb-1">REASON PROVIDED</div>
                          <div className="text-sm text-yellow-200">{log.reason}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">Technical Details</h4>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-gray-400">Audit ID:</span>
                          <span className="text-gray-300 ml-2 font-mono text-xs">{log.id}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Admin UID:</span>
                          <span className="text-gray-300 ml-2 font-mono text-xs">{log.adminUid}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">IP Address:</span>
                          <span className="text-gray-300 ml-2">{log.ipAddress || 'Not recorded'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Resource:</span>
                          <span className="text-gray-300 ml-2 font-mono text-xs">{log.resource}</span>
                        </div>
                        {log.regulatoryTag && log.regulatoryTag.length > 0 && (
                          <div className="col-span-2">
                            <span className="text-gray-400">Regulatory Tags:</span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {log.regulatoryTag.map((tag: string, idx: number) => (
                                <span key={idx} className="px-2 py-1 bg-purple-900 text-purple-200 rounded text-xs font-mono">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {log.affectedUsers && log.affectedUsers.length > 0 && (
                          <div className="col-span-2">
                            <span className="text-gray-400">Affected Users:</span>
                            <span className="text-gray-300 ml-2">{log.affectedUsers.length} users</span>
                          </div>
                        )}
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="col-span-2">
                            <span className="text-gray-400">Additional Metadata:</span>
                            <pre className="mt-1 text-xs text-gray-300 bg-gray-900 p-2 rounded overflow-x-auto font-mono">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogPage;
